import React from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, ChevronRight, PhoneForwarded, Settings2, Voicemail, WandSparkles } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
interface CallsEmptyStateProps {
  onLoadSampleCalls: () => void;
  onOpenCallerExperience: () => void;
}
interface GuidanceStep {
  icon: typeof PhoneForwarded;
  title: string;
  detail: string;
}
const GUIDANCE_STEPS: GuidanceStep[] = [{
  icon: PhoneForwarded,
  title: 'Calls arrive on your number',
  detail: 'Forwarding is active — Vexu picks up when you can’t.'
}, {
  icon: Voicemail,
  title: 'Vexu answers and takes notes',
  detail: 'You get the full transcript plus a short summary.'
}, {
  icon: CalendarDays,
  title: 'Bookings land in your calendar',
  detail: 'Meetings your AI schedules show up on the call.'
}];
export function CallsEmptyState({
  onLoadSampleCalls,
  onOpenCallerExperience
}: CallsEmptyStateProps) {
  const {
    isLight
  } = useTheme();
  const hairline = isLight ? 'bg-slate-200/70' : 'bg-white/[0.07]';
  return <div className="px-5 pt-6 pb-8" data-id="element-156" data-mp="2blaf299pf6qw">
      <motion.section data-tour="calls-list-item" initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className={`relative overflow-hidden rounded-[28px] border ${isLight ? 'border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_40px_-18px_rgba(15,23,42,0.18)]' : 'border-white/[0.06] bg-[#0F0F0F] shadow-[0_20px_60px_-30px_rgba(0,0,0,0.9)]'}`} data-id="element-157" data-mp="2bmbrah2ghza7">
        {/* Ambient halo */}
        <div aria-hidden="true" className={`pointer-events-none absolute -top-24 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full blur-3xl ${isLight ? 'bg-vexu/[0.09]' : 'bg-vexu/[0.10]'}`} data-id="element-158" data-mp="1ntva08qjmbby" />

        <div className="relative px-6 pt-10 pb-7 text-center" data-id="element-159" data-mp="19w6n6vcx0u49">
          {/* Emblem */}
          <div className="relative mx-auto mb-6 grid h-20 w-20 place-items-center" data-id="element-160" data-mp="19yj4r7erh2gs">
            {[0, 1].map(i => <motion.span key={i} aria-hidden="true" animate={{
            scale: [0.85, 1.25],
            opacity: [0.35, 0]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeOut',
            delay: i * 1.5
          }} className={`absolute inset-0 rounded-full border ${isLight ? 'border-vexu/30' : 'border-vexu/25'}`} data-id="element-161" data-mp="157e2bvat7u0q" />)}
            <span className={`relative grid h-16 w-16 place-items-center rounded-full ${isLight ? 'bg-vexu/[0.08] text-vexu-700 ring-1 ring-inset ring-vexu/15' : 'bg-vexu/[0.10] text-vexu ring-1 ring-inset ring-vexu/20'}`} data-id="element-162" data-mp="1998y6k9z1f74">
              <Voicemail className="h-[26px] w-[26px]" strokeWidth={1.6} data-id="element-163" data-mp="21x4vnr7d7b4y" />
            </span>
          </div>

          <h2 className={`text-[19px] font-semibold tracking-[-0.01em] ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-164" data-mp="26q7oty88cikm">
            No calls yet
          </h2>
          <p className={`mx-auto mt-2.5 max-w-[17rem] text-[12.5px] leading-[1.65] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-165" data-mp="3mf5x0jhyy0c1">
            Vexu is standing by. Every answered call appears here with its
            transcript, summary, and any meetings booked.
          </p>
        </div>

        <div className={`mx-6 h-px ${hairline}`} data-id="element-166" data-mp="38gpnylwmvxqq" />

        {/* Timeline */}
        <ol className="relative px-6 py-7" data-id="element-167" data-mp="33i4pos0xzo4x">
          <span aria-hidden="true" className={`absolute left-[35px] top-[42px] bottom-[42px] w-px ${isLight ? 'bg-gradient-to-b from-slate-200 via-slate-200 to-transparent' : 'bg-gradient-to-b from-white/10 via-white/[0.07] to-transparent'}`} data-id="element-168" data-mp="33jfsaoinipug" />
          {GUIDANCE_STEPS.map((step, index) => <motion.li key={step.title} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.14 + index * 0.08,
          duration: 0.34
        }} className={`relative flex items-start gap-3.5 ${index === 0 ? '' : 'mt-5'}`} data-id="element-169" data-mp="spl49twbs5fm" data-mpi={index}>
              <span className={`relative z-10 grid h-[34px] w-[34px] flex-shrink-0 place-items-center rounded-full ${isLight ? 'bg-white text-slate-600 ring-1 ring-slate-200' : 'bg-[#171717] text-zinc-300 ring-1 ring-white/[0.08]'}`} data-id="element-170" data-mp="1wyvaxifzd93x">
                <step.icon className="h-[15px] w-[15px]" strokeWidth={1.7} data-id="element-171" data-mp="2azfs6a68ywvb" />
              </span>
              <span className="min-w-0 pt-1" data-id="element-172" data-mp="2fqo1z7bhok90">
                <span className={`block text-[12.5px] font-semibold leading-tight tracking-[-0.005em] ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-173" data-mp="2fqxtizc6mzgt">
                  {step.title}
                </span>
                <span className={`mt-1 block text-[11.5px] leading-[1.55] ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-174" data-mp="2unoieeo8css6">
                  {step.detail}
                </span>
              </span>
            </motion.li>)}
        </ol>

        <div className={`mx-6 h-px ${hairline}`} data-id="element-175" data-mp="e3bf00jbqaiv" />

        <div className="px-6 pt-6 pb-6" data-id="element-176" data-mp="936ve1n4czof">
          <button type="button" onClick={onLoadSampleCalls} className={`flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3.5 text-[13px] font-semibold tracking-[-0.005em] transition-all active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu focus-visible:ring-offset-2 ${isLight ? 'bg-vexu text-white shadow-[0_8px_22px_-8px_rgba(0,200,83,0.55)] hover:bg-vexu-600 focus-visible:ring-offset-white' : 'bg-vexu text-black shadow-[0_8px_24px_-10px_rgba(0,200,83,0.8)] hover:bg-vexu-400 focus-visible:ring-offset-[#0F0F0F]'}`} data-id="element-177" data-mp="9487m9fvfs7q">
            <WandSparkles className="h-4 w-4" strokeWidth={1.8} data-id="element-178" data-mp="1fquarke5gw5j" />
            Simulate call history
          </button>
          <p className={`mt-2.5 text-center text-[10.5px] ${isLight ? 'text-slate-400' : 'text-zinc-600'}`} data-id="element-179" data-mp="39xwjjeh8guez">
            Loads example calls so you can explore the experience.
          </p>
        </div>

        <div className={`mx-6 h-px ${hairline}`} data-id="element-180" data-mp="307tq5d679eg4" />

        <button type="button" onClick={onOpenCallerExperience} className={`group flex w-full items-center gap-3 px-6 py-4 text-left transition-colors ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.03]'}`} data-id="element-181" data-mp="357g2zli48soe">
          <span className={`grid h-8 w-8 flex-shrink-0 place-items-center rounded-full ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/[0.05] text-zinc-400'}`} data-id="element-182" data-mp="2vb3b8t64sowa">
            <Settings2 className="h-[15px] w-[15px]" strokeWidth={1.7} data-id="element-183" data-mp="2m9n18mj8aosl" />
          </span>
          <span className="min-w-0 flex-1" data-id="element-184" data-mp="fjfhf6mezchp">
            <span className={`block text-[12.5px] font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-185" data-mp="fgkun8fxhfl4">
              Set up your caller experience
            </span>
            <span className={`mt-0.5 block text-[11px] ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-186" data-mp="24x6lp685r62e">
              Choose the greeting, tone, and booking rules.
            </span>
          </span>
          <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-transform group-hover:translate-x-0.5 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} strokeWidth={2} data-id="element-187" data-mp="29y2r5o85r09x" />
        </button>
      </motion.section>
    </div>;
}